import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useMediaQuery } from 'usehooks-ts';

import { SearchBar } from 'shared/components/ui/SearchBar/SearchBar';
import { SearchBarMobile } from 'shared/components/ui/SearchBar/SearchBarMobile';
import { BadgeSelect } from 'shared/components/ui/badge-select/badge-select';
import { BadgeSelectMobile } from 'shared/components/ui/badge-select/badge-select-mobile';
import { BadgeSwitchMobile } from 'shared/components/ui/badge-switch/badge-switch-mobile';
import { coinIcons } from 'shared/constants';
import { CHAINS_TOKENS_CONFIG } from 'shared/constants/tokens';
import { useAccount } from 'shared/hooks/use-web3';
import { CurrencyFilter, SortOrder } from 'shared/types/games.types';

import ClockSmallIcon from 'assets/clock-small.svg?react';
import ClockIcon from 'assets/clock.svg?react';
import FilterSmallIcon from 'assets/filter-small.svg?react';
import FilterIcon from 'assets/filter.svg?react';

interface Option {
  label: string;
  value: string;
}

interface ITableBarProps {
  sorting: { [key: string]: string };
  handleSetSorting: React.Dispatch<React.SetStateAction<{ [key: string]: string }>>;
}

// Type guard to check if a key exists in an object
function isValidChainName(name: string): name is keyof typeof CHAINS_TOKENS_CONFIG {
  return name in CHAINS_TOKENS_CONFIG;
}

function isValidCurrency(currency: string): currency is keyof typeof coinIcons {
  return currency in coinIcons;
}

export const TableBar: React.FC<ITableBarProps> = ({ sorting, handleSetSorting }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 767px)');

  const { chain } = useAccount();
  const [searchOpen, setSearchOpen] = useState(false);

  const sortTimeOptions: Option[] = [
    { label: t('filters.oldest'), value: SortOrder.ASC },
    { label: t('filters.newest'), value: SortOrder.DESC },
  ];

  const sortBetOptions: Option[] = [
    {
      label: isMobile ? t('filters.lowToHighMobile') : t('filters.lowToHigh'),
      value: SortOrder.ASC,
    },
    {
      label: isMobile ? t('filters.highToLowMobile') : t('filters.highToLow'),
      value: SortOrder.DESC,
    },
    { label: t('filters.default'), value: SortOrder.DEFAULT },
  ];

  const currencyOptions: Option[] = useMemo(() => {
    if (!chain || !isValidChainName(chain.name)) {
      return [{ label: t('filters.all'), value: CurrencyFilter.ALL }];
    }
    return [
      { label: t('filters.all'), value: CurrencyFilter.ALL },
      ...CHAINS_TOKENS_CONFIG[chain.name].map((token) => ({
        label: token.symbol,
        value: token.symbol,
      })),
    ];
  }, [chain, t]);

  const handleSortingChange = (field: string, value: string) => {
    handleSetSorting((prev) => ({ ...prev, [field]: value }));
  };

  //DESKTOP LAYOUT
  const DesktopLayout = (
    <div className="flex w-full flex-col justify-center gap-4 sm:items-center md:flex-row">
      <SearchBar setSearch={(value: string) => handleSortingChange('search', value)} />

      <div className="relative flex items-center justify-center gap-4">
        <BadgeSelect
          className="min-w-[252px]"
          value={sorting.roomId}
          setValue={(value) => handleSortingChange('roomId', value)}
          options={sortTimeOptions}
          icon={<ClockIcon />}
        />

        <BadgeSelect
          className="min-w-[172px]"
          value={sorting.currency}
          setValue={(value) => handleSortingChange('currency', value)}
          options={currencyOptions}
          icon={
            isValidCurrency(sorting.currency) ? (
              <img src={coinIcons[sorting.currency]} />
            ) : undefined
          }
        />

        <BadgeSelect
          className="min-w-[216px]"
          value={sorting.betAmount}
          setValue={(value) => handleSortingChange('betAmount', value)}
          options={sortBetOptions}
          icon={<FilterIcon />}
        />
      </div>
    </div>
  );

  // MOBILE LAYOUT
  const MobileLayout = (
    <div className="flex justify-center gap-2">
      <div className="relative flex items-center justify-center gap-3">
        <BadgeSwitchMobile
          title="Date Created"
          value={sorting.roomId}
          setValue={(value) => handleSortingChange('roomId', value)}
          options={sortTimeOptions}
          icon={<ClockSmallIcon />}
        />

        <div className="h-[1.25rem] w-[1px] bg-indigo" />

        <BadgeSwitchMobile
          title="Bet"
          value={sorting.betAmount}
          setValue={(value) => handleSortingChange('betAmount', value)}
          options={sortBetOptions}
          icon={<FilterSmallIcon />}
        />

        <div className="h-[1.25rem] w-[1px] bg-indigo" />

        <BadgeSelectMobile
          value={sorting.currency}
          setValue={(value) => handleSortingChange('currency', value)}
          options={currencyOptions}
          icon={
            isValidCurrency(sorting.currency) ? (
              <img src={coinIcons[sorting.currency]} />
            ) : undefined
          }
        />

        <SearchBarMobile
          setSearch={(value) => handleSortingChange('search', value)}
          setSearchOpen={() => setSearchOpen(!searchOpen)}
          searchOpen={searchOpen}
        />
      </div>
    </div>
  );

  return isMobile ? MobileLayout : DesktopLayout;
};
